import {
  IAIWorkspace,
  IAIWorkspaceRequest,
  useUpdateCreatorFitMasterPrompt,
  useUpdateDraftVetMasterPrompt,
  useUpdateScriptVetMasterPrompt,
} from "@entities/aiWorkspace";
import { useState } from "react";
import { ScriptGenerationMasterPromptForm } from "../ScriptGenerationTab/ScriptGenerationMasterPromptForm";
import { readScriptGenerationMasterPrompt } from "../ScriptGenerationTab/types";
import { VetPromptModule } from "../promptGuides";
import { MasterPromptBlock } from "./MasterPromptBlock";
import styles from "./styles.module.scss";

type OpenModule = VetPromptModule | "script-generation";

type OverviewMasterPromptsProps = {
  workspace: IAIWorkspace;
  requests: IAIWorkspaceRequest[];
};

export function OverviewMasterPrompts({
  workspace,
  requests,
}: OverviewMasterPromptsProps) {
  const [creatorVersion, setCreatorVersion] = useState(0);
  const [scriptVersion, setScriptVersion] = useState(0);
  const [draftVersion, setDraftVersion] = useState(0);
  const creator = useUpdateCreatorFitMasterPrompt({
    onSuccess: () => setCreatorVersion((version) => version + 1),
  });
  const script = useUpdateScriptVetMasterPrompt({
    onSuccess: () => setScriptVersion((version) => version + 1),
  });
  const draft = useUpdateDraftVetMasterPrompt({
    onSuccess: () => setDraftVersion((version) => version + 1),
  });

  const [openModule, setOpenModule] = useState<OpenModule | null>(null);
  const save = (
    prompt: string,
    update: (payload: { aiWorkspaceId: string; prompt: string }) => void
  ) => {
    update({ aiWorkspaceId: workspace._id, prompt });
  };

  const cards: {
    title: string;
    moduleId: VetPromptModule;
    noun: string;
    savedPrompt: string;
    hasPriorResults: boolean;
    isSaving: boolean;
    saveVersion: number;
    onSave: (prompt: string) => void;
  }[] = [
    {
      title: "Creator Fit",
      moduleId: "creator-fit",
      noun: "creators",
      savedPrompt: workspace.creatorFitMasterPrompt?.prompt || "",
      hasPriorResults: requests.some(
        (request) => request.type === "creator-fit"
      ),
      isSaving: creator.isUpdating,
      saveVersion: creatorVersion,
      onSave: (prompt) => save(prompt, creator.update),
    },
    {
      title: "Script Vet",
      moduleId: "script-vet",
      noun: "scripts",
      savedPrompt: workspace.scriptVetMasterPrompt?.prompt || "",
      hasPriorResults: requests.some(
        (request) => request.type === "script-vet"
      ),
      isSaving: script.isUpdating,
      saveVersion: scriptVersion,
      onSave: (prompt) => save(prompt, script.update),
    },
    {
      title: "Draft Vet",
      moduleId: "draft-vet",
      noun: "drafts",
      savedPrompt: workspace.draftVetMasterPrompt?.prompt || "",
      hasPriorResults: requests.some((request) => request.type === "draft-vet"),
      isSaving: draft.isUpdating,
      saveVersion: draftVersion,
      onSave: (prompt) => save(prompt, draft.update),
    },
  ];
  const openCard = cards.find((card) => card.moduleId === openModule);
  const scriptPrompt = readScriptGenerationMasterPrompt(
    workspace.scriptGenerationMasterPrompt
  );
  const scriptConfigured = promptHasText(scriptPrompt?.brief || "");
  const scriptSelected = openModule === "script-generation";

  return (
    <div className={styles.masterPrompts}>
      <div className={styles.promptCards}>
        {cards.map((card) => {
          const configured = promptHasText(card.savedPrompt);
          const selected = openModule === card.moduleId;
          return (
            <button
              key={card.moduleId}
              type="button"
              className={`${styles.promptCard} ${selected ? styles.promptCardSelected : ""}`}
              onClick={() => setOpenModule(selected ? null : card.moduleId)}
            >
              <span className={styles.promptCardTitle}>{card.title}</span>
              <span
                className={
                  configured ? styles.promptSaved : styles.promptMissing
                }
              >
                {configured ? "Prompt saved" : "Not configured"}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          className={`${styles.promptCard} ${scriptSelected ? styles.promptCardSelected : ""}`}
          onClick={() => setOpenModule(scriptSelected ? null : "script-generation")}
        >
          <span className={styles.promptCardTitle}>Script Generation</span>
          <span className={scriptConfigured ? styles.promptSaved : styles.promptMissing}>
            {scriptConfigured ? "Prompt saved" : "Not configured"}
          </span>
        </button>
      </div>
      {scriptSelected ? (
        <section className={styles.masterPromptBlock}>
          <h4 className={styles.masterPromptTitle}>Script Generation</h4>
          <ScriptGenerationMasterPromptForm
            aiWorkspaceId={workspace._id}
            saved={scriptPrompt}
            showHeading={false}
            framed={false}
          />
        </section>
      ) : null}
      {openCard ? (
        <MasterPromptBlock
          title={openCard.title}
          moduleId={openCard.moduleId}
          aiWorkspaceId={workspace._id}
          noun={openCard.noun}
          savedPrompt={openCard.savedPrompt}
          hasPriorResults={openCard.hasPriorResults}
          isSaving={openCard.isSaving}
          saveVersion={openCard.saveVersion}
          onSave={openCard.onSave}
        />
      ) : null}
    </div>
  );
}

function promptHasText(value: string) {
  return (
    value
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim().length > 0
  );
}
